import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { apiFetch, setStoredJwt, getStoredJwt } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const u = await apiFetch('/api/auth/me');
      setUser(u);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Emergent Google OAuth callback, let the caller handle
    // the session_id exchange first — don't preempt with /auth/me.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    // Anonymous visitors have no stored JWT — skip /auth/me so we don't spam
    // the console with expected 401s for logged-out users.
    if (!getStoredJwt()) {
      setUser(null);
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const register = async (email, password, name) => {
    const res = await apiFetch('/api/auth/register', { method: 'POST', body: { email, password, name } });
    setStoredJwt(res.access_token);
    setUser(res.user);
    return res.user;
  };

  const login = async (email, password) => {
    const res = await apiFetch('/api/auth/login', { method: 'POST', body: { email, password } });
    setStoredJwt(res.access_token);
    setUser(res.user);
    return res.user;
  };

  const googleExchange = async (sessionId) => {
    const res = await apiFetch('/api/auth/google/session', { method: 'POST', body: { session_id: sessionId } });
    // Google path also returns an access_token equal to session_token so
    // cross-origin browsers that block third-party cookies still work.
    setStoredJwt(res.access_token);
    setUser(res.user);
    return res.user;
  };

  const googleVerify = async (credential) => {
    const res = await apiFetch('/api/auth/google/verify', { method: 'POST', body: { credential } });
    setStoredJwt(res.access_token);
    setUser(res.user);
    return res.user;
  };

  const logout = async () => {
    try { await apiFetch('/api/auth/logout', { method: 'POST' }); } catch {}
    setStoredJwt(null);
    setUser(null);
  };

  const refresh = checkAuth;

  return (
    <AuthContext.Provider value={{ user, loading, login, register, googleExchange, googleVerify, logout, refresh, isAuthenticated: !!user, hasJwt: !!getStoredJwt() }}>
      {children}
    </AuthContext.Provider>
  );
};
